import React, { useState, useEffect } from 'react';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { apartmentsApi, type ApartmentUsageType, type Building } from '../../api/apartmentsApi';
import { applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import AddApartmentUsageTypeModal from '../../components/modals/AddApartmentUsageTypeModal';
import { getApiErrorMessage } from '../../utils/apiErrors';

const ApartmentUsageTypes: React.FC = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const [usageTypes, setUsageTypes] = useState<ApartmentUsageType[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(25);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUsageType, setEditingUsageType] = useState<ApartmentUsageType | null>(null);
  const [exporting, setExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importMode, setImportMode] = useState<'skip' | 'replace'>('skip');
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadBuildings();
  }, []);

  useEffect(() => {
    if (selectedBuildingId) {
      loadUsageTypes();
    } else {
      setUsageTypes([]);
      setLoading(false);
    }
  }, [selectedBuildingId]);

  const loadBuildings = async () => {
    try {
      const data = await apartmentsApi.getBuildings();
      setBuildings(data);
      setSelectedBuildingId((prev) => applyDefaultBuildingSelection(data, user, prev));
    } catch (err) {
      console.error('Error loading buildings:', err);
      setError(t('failedToLoad'));
    }
  };

  const loadUsageTypes = async () => {
    if (!selectedBuildingId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await apartmentsApi.getApartmentUsageTypes(selectedBuildingId);
      setUsageTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading usage types:', err);
      setError(t('errorLoadingUsageTypes'));
      setUsageTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('confirmDeleteUsageType'))) {
      try {
        await apartmentsApi.deleteApartmentUsageType(id);
        await loadUsageTypes();
      } catch (err) {
        console.error('Error deleting usage type:', err);
        setError(getApiErrorMessage(err, t('errorDeletingUsageType')));
      }
    }
  };

  const handleBuildingChange = (buildingId: string) => {
    selectBuilding(user?.id, buildingId, setSelectedBuildingId);
    setCurrentPage(1);
    setSearchTerm('');
  };

  const handleExport = async () => {
    if (!selectedBuildingId) {
      setError(t('pleaseSelectBuilding'));
      return;
    }

    try {
      setExporting(true);
      setError(null);
      await apartmentsApi.exportApartmentUsageTypes(selectedBuildingId);
      alert(t('exportUsageTypesSuccess'));
    } catch (err) {
      setError(getApiErrorMessage(err, t('failedToLoad')));
    } finally {
      setExporting(false);
    }
  };

  const openImportModal = () => {
    setImportFile(null);
    setImportError(null);
    setImportSuccess(null);
    setImportMode('skip');
    setShowImportModal(true);
  };

  const handleImport = async () => {
    if (!selectedBuildingId) {
      setImportError(t('pleaseSelectBuilding'));
      return;
    }
    if (!importFile) {
      setImportError(t('clickToSelectFile'));
      return;
    }

    try {
      setImportLoading(true);
      setImportError(null);
      setImportSuccess(null);
      const result = await apartmentsApi.importApartmentUsageTypes(selectedBuildingId, importFile, importMode);

      let message = result.message;
      if (result.skippedCount > 0 && result.skippedCodes.length > 0) {
        message += ` (${result.skippedCodes.join(', ')})`;
      }
      if (result.errors.length > 0) {
        message += `\n${result.errors.join('\n')}`;
      }

      if (result.success) {
        setImportSuccess(message);
        await loadUsageTypes();
      } else {
        setImportError(message);
      }
    } catch (err) {
      setImportError(getApiErrorMessage(err, t('failedToLoad')));
    } finally {
      setImportLoading(false);
    }
  };

  const openEditModal = (usageType: ApartmentUsageType) => {
    setEditingUsageType(usageType);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingUsageType(null);
  };

  const filteredTypes = usageTypes.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.ceil(filteredTypes.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const currentTypes = filteredTypes.slice(startIndex, startIndex + recordsPerPage);
  const selectedBuilding = buildings.find(b => b.id === selectedBuildingId);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">{t('loadingUsageTypes')}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('apartmentUsageTypes')}</h1>
        <div className="flex gap-3">
          <button
            onClick={handleExport}
            disabled={exporting || !selectedBuildingId}
            className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
          >
            {exporting ? t('loading') : t('exportUsageTypes')}
          </button>
          <button
            onClick={openImportModal}
            disabled={!selectedBuildingId}
            className="inline-flex items-center px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
          >
            {t('importUsageTypes')}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {t('addApartmentUsageType')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">{error}</div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        {shouldShowBuildingSelector(buildings) && (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('selectBuilding')}</label>
            <select
              value={selectedBuildingId}
              onChange={(e) => handleBuildingChange(e.target.value)}
                disabled={!needsBuildingSelection(buildings)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('selectBuildingPlaceholder')}</option>
              {buildings.map((building) => (
                <option key={building.id} value={building.id}>{building.name}</option>
              ))}
            </select>
          </>
        )}
        {selectedBuilding && (
          <div className="text-sm text-gray-600 mt-2">
            <span className="font-medium">{t('operator')}:</span> {selectedBuilding.operator?.name}
          </div>
        )}
      </div>

      {!selectedBuildingId ? (
        <div className="text-center py-12 text-gray-500">{t('pleaseSelectBuildingToViewUsageTypes')}</div>
      ) : usageTypes.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-4">{t('noUsageTypes')}</div>
          <button onClick={() => setShowAddModal(true)} className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg">
            {t('addFirstUsageType')}
          </button>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('search')}
              className="w-full max-w-md border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('stt')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('code')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('name')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('description')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('usageTypeManagementFeeCode')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('default')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('apartment')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('resident')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('status')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentTypes.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm">{startIndex + index + 1}</td>
                    <td className="px-6 py-4 text-sm font-medium text-blue-600">{item.code}</td>
                    <td className="px-6 py-4 text-sm">{item.name}</td>
                    <td className="px-6 py-4 text-sm max-w-xs truncate">{item.description || '-'}</td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-700">{item.managementFeeCode || '-'}</td>
                    <td className="px-6 py-4 text-sm">{item.isDefault ? t('yes') : t('no')}</td>
                    <td className="px-6 py-4 text-sm">{item._count?.apartments || 0}</td>
                    <td className="px-6 py-4 text-sm">{item.residentCount || 0}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        item.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {item.status === 'ACTIVE' ? t('active') : t('inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex gap-3">
                        <button onClick={() => openEditModal(item)} className="text-blue-600 hover:text-blue-800">
                          {t('edit')}
                        </button>
                        <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800">
                          {t('delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="text-sm text-gray-600 mt-4 text-center">
              {t('page')} {currentPage} {t('of')} {totalPages}
            </div>
          )}
        </>
      )}

      <AddApartmentUsageTypeModal
        isOpen={showAddModal || Boolean(editingUsageType)}
        onClose={closeModal}
        onSuccess={loadUsageTypes}
        selectedBuildingId={selectedBuildingId}
        usageType={editingUsageType}
      />

      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">{t('importUsageTypes')}</h2>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-200 rounded-full transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              {!selectedBuildingId ? (
                <div className="text-sm text-gray-600">{t('pleaseSelectBuilding')}</div>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-4">{t('importUsageTypesHint')}</p>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('selectBuilding')}</label>
                    <div className="text-sm text-gray-800 font-medium">{selectedBuilding?.name}</div>
                  </div>

                  <div className="mb-4 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="importMode"
                        checked={importMode === 'skip'}
                        onChange={() => setImportMode('skip')}
                      />
                      <span className="text-sm text-gray-700">{t('importUsageTypesModeSkip')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="importMode"
                        checked={importMode === 'replace'}
                        onChange={() => setImportMode('replace')}
                      />
                      <span className="text-sm text-gray-700">{t('importUsageTypesModeReplace')}</span>
                    </label>
                  </div>

                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center mb-4">
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                      className="hidden"
                      id="usage-type-import-file"
                    />
                    <label htmlFor="usage-type-import-file" className="cursor-pointer flex flex-col items-center space-y-2">
                      <span className="text-sm text-gray-700">
                        {importFile ? importFile.name : t('clickToSelectFile')}
                      </span>
                      <span className="text-xs text-gray-500">
                        <span className="font-medium text-green-600">{t('excelRecommended')}</span> • {t('csvAlsoSupported')}
                      </span>
                    </label>
                  </div>

                  {importError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm whitespace-pre-line">
                      {importError}
                    </div>
                  )}

                  {importSuccess && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 text-sm whitespace-pre-line">
                      {importSuccess}
                    </div>
                  )}

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowImportModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                    >
                      {t('close')}
                    </button>
                    <button
                      onClick={handleImport}
                      disabled={importLoading}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      {importLoading ? t('importLoading') : t('importUsageTypes')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApartmentUsageTypes;
